import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFacilityBilling } from '../hooks/useFacilityBilling';
import { facilityApi } from '../api/api';
import { summarizeBilling, planDetails, BILLING_EMAIL } from '../utils/billingStatus';
import RoleGuard from './facility/RoleGuard';
import BillingPlanCard from './billing/BillingPlanCard';
import BillingStatusCard from './billing/BillingStatusCard';
import BillingSeatsCard from './billing/BillingSeatsCard';
import BillingUsageCard from './billing/BillingUsageCard';
import BillingActionsCard from './billing/BillingActionsCard';
import '../styles/BillingPage.css';

const FACILITY_ID_RE = /^[A-Za-z0-9_-]{1,128}$/;

export default function BillingPage() {
    const { isAdmin, facilityId: ownFacilityId, profileReady } = useAuth();
    const [searchParams] = useSearchParams();

    if (!profileReady) return null;
    // Site admins may inspect any facility; everyone else is pinned to their own.
    // Firestore document ids cannot contain "/", and anything odd here is a typo
    // in the address bar, so ignore it rather than let the SDK throw.
    const requested = searchParams.get('facility');
    const valid = Boolean(requested && FACILITY_ID_RE.test(requested));
    const facilityId = (isAdmin && valid ? requested : null) || ownFacilityId;
    // RoleGuard first: a non-admin must be told "not authorized", never "contact billing".
    // With facilityId null, RoleGuard admits site admins and facility admins whose own
    // facilityId is also null, and denies everyone else.
    return (
        <RoleGuard requireFacilityAdmin facilityId={facilityId}>
            {facilityId ? (
                <BillingPageInner facilityId={facilityId} />
            ) : (
                <NoFacilityMessage isAdmin={isAdmin} requested={requested} valid={valid} />
            )}
        </RoleGuard>
    );
}

function NoFacilityMessage({ isAdmin, requested, valid }) {
    let text;
    if (isAdmin && requested && !valid) {
        text = <>Facility id <code>{requested}</code> is not recognized. Check the address and try again.</>;
    } else if (isAdmin) {
        text = <>Add <code>?facility=&lt;id&gt;</code> to the address to inspect an agency's billing.</>;
    } else {
        text = <>Your account is not linked to an agency. Contact <a href={`mailto:${BILLING_EMAIL}`}>{BILLING_EMAIL}</a>.</>;
    }
    return (
        <div className="billing-page">
            <div className="role-guard-denied">{text}</div>
        </div>
    );
}

function BillingPageInner({ facilityId }) {
    const { billing, loading, error } = useFacilityBilling(facilityId);
    const [facility, setFacility] = useState(null);
    useEffect(() => {
        let cancelled = false;
        setFacility(null); // never show the previous agency's name beside this one's numbers
        facilityApi.get(facilityId)
            .then((f) => { if (!cancelled) setFacility(f); })
            .catch(() => { if (!cancelled) console.warn(`BillingPage: could not load facility ${facilityId}; showing its id`); });
        return () => { cancelled = true; };
    }, [facilityId]);

    // Billing state depends on the clock as well as the doc. Re-derive once a
    // minute so a trial that ends while the page is open flips to "Trial ended"
    // at the same time the backend starts answering 402.
    const [nowMs, setNowMs] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNowMs(Date.now()), 60_000);
        return () => clearInterval(id);
    }, []);

    const summary = summarizeBilling(billing, nowMs);
    const { seatLimit, caseAllowance } = planDetails(billing?.plan, billing ?? {});
    const facilityName = facility?.name || facilityId;

    return (
        <div className="billing-page">
            <header className="billing-page-header">
                <div className="billing-page-eyebrow">{facilityName}</div>
                <h1 className="billing-page-title">Billing</h1>
                <p className="billing-page-subtitle">Your agency plan, seats, and usage.</p>
            </header>
            {loading ? <div className="billing-loading">Loading…</div>
             : error ? (
                <div className="billing-error-panel" role="alert">
                    Could not load billing for this agency. Reload the page or contact{' '}
                    <a href={`mailto:${BILLING_EMAIL}`}>{BILLING_EMAIL}</a>.
                </div>
            ) : (
                <div className="billing-grid">
                    <BillingPlanCard billing={billing} />
                    <BillingStatusCard summary={summary} nowMs={nowMs} />
                    <BillingSeatsCard facilityId={facilityId} seatLimit={seatLimit} />
                    <BillingUsageCard facilityId={facilityId} caseAllowance={caseAllowance} />
                    <BillingActionsCard facilityName={facilityName} status={summary.status} />
                </div>
            )}
        </div>
    );
}
